import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Bag from '../../assets/bag.png';
import { useCart } from '../../context/CartContext';
import SearchModal from '../Search/SearchModal';
import CartButtonAndPreview from './CartButtonAndPreview';

/**
 * Unified Header Component
 * @param {string} variant - 'transparent' (home page) | 'white' (other pages)
 */
const Header = ({ variant = 'transparent' }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileActiveSubmenu, setMobileActiveSubmenu] = useState(null);
  const [products, setProducts] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const { cartItems } = useCart();

  const isWhite = variant === 'white';

  // Fetch products
  useEffect(() => {
    axios
      .get('https://admin.bodiosyakdown.com/api/products')
      .then((res) => setProducts(res.data))
      .catch((err) => console.error(err));
  }, []);

  // Scroll handler (only for transparent variant)
  useEffect(() => {
    if (isWhite) return;
    
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isWhite]);

  // Mobile menu body scroll lock
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [mobileMenuOpen]);

  // Reset mobile submenu when menu closes
  useEffect(() => {
    if (!mobileMenuOpen) {
      setMobileActiveSubmenu(null);
    }
  }, [mobileMenuOpen]);

  // Menu hover handlers (desktop)
  const handleMenuEnter = (menu) => {
    clearTimeout(menuTimeoutRef.current);
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };

  const handleDropdownEnter = () => {
    clearTimeout(menuTimeoutRef.current);
  };

  const handleDropdownLeave = () => {
    setActiveMenu(null);
  };

  // Navigation
  const navigateTo = (path) => {
    navigate(path);
    setActiveMenu(null);
    setMobileMenuOpen(false);
    setMobileActiveSubmenu(null);
  };

  // Mobile submenu toggle
  const toggleMobileSubmenu = (menu) => {
    setMobileActiveSubmenu(mobileActiveSubmenu === menu ? null : menu);
  };

  // Style helpers
  const getTextColor = () => {
    if (isWhite || isScrolled || activeMenu) return 'text-black';
    return 'text-white';
  };

  const getHeaderBg = () => {
    if (isWhite) return 'bg-[#F7F4EF] shadow-[0px_1px_0px_rgba(0,0,0,0.15)]';
    if (isScrolled || activeMenu || mobileMenuOpen) return 'bg-[#F7F4EF]';
    return 'bg-transparent';
  };

  const getMobileIconColor = () => {
    if (isWhite || mobileMenuOpen) return 'text-black';
    return 'text-white';
  };

  // Menu data
  const discoverItems = [
    { label: 'Bodios', path: '/bodios' },
    { label: 'About us', path: '/about' },
    { label: 'Yak', path: '/yak' },
    { label: 'Social Responsibility', path: '/social' },
    { label: 'Journal', path: '/journal' },
  ];

  const boutiqueItems = [
    { label: 'Women', path: '/category/women' },
    { label: 'Men', path: '/category/men' },
    { label: 'Home', path: '/category/home' },
    { label: 'Accessories', path: '/category/accessories' },
  ];

  // Desktop menu with images
  const discoverItemsWithImages = [
    { label: 'Bodios', path: '/bodios', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/7f8f5cc0c336d3a3e4a721779edfedac769a928b7db54b2a5ba455d39585c814?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
    { label: 'About us', path: '/about', image: 'https://cdn.builder.io/api/v1/image/assets/9804d3a088fa478e8672485264720a14/03c2838850d74ba3e363486605acb44a1fd3c8cf6f839e931ad72c6e6d26fd51?apiKey=9804d3a088fa478e8672485264720a14&' },
    { label: 'Yak', path: '/yak', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/ef2946374d78dd117795d93c5afb3327d68f39f735697e142363cb5f7772f778?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
    { label: 'Social Responsibility', path: '/social', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/4a18a0c33504f9791d6e77e10ce10d994b8ac54a99c459800b8dc232fd9983fa?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
    { label: 'Journal', path: '/journal', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/f1d63fded03a1907c9f7b643bfc930bd1d62dfe967494fcaf93a84677950f84f?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
  ];

  const boutiqueItemsWithImages = [
    { label: 'Women', path: '/category/women', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/7f8f5cc0c336d3a3e4a721779edfedac769a928b7db54b2a5ba455d39585c814?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
    { label: 'Men', path: '/category/men', image: 'https://cdn.builder.io/api/v1/image/assets/9804d3a088fa478e8672485264720a14/03c2838850d74ba3e363486605acb44a1fd3c8cf6f839e931ad72c6e6d26fd51?apiKey=9804d3a088fa478e8672485264720a14&' },
    { label: 'Home', path: '/category/home', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/ef2946374d78dd117795d93c5afb3327d68f39f735697e142363cb5f7772f778?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
    { label: 'Accessories', path: '/category/accessories', image: 'https://cdn.builder.io/api/v1/image/assets/TEMP/4a18a0c33504f9791d6e77e10ce10d994b8ac54a99c459800b8dc232fd9983fa?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1' },
  ];

  const currentMenuItems = activeMenu === 'discover' ? discoverItemsWithImages : activeMenu === 'boutique' ? boutiqueItemsWithImages : [];
  const cartCount = cartItems.reduce((s, x) => s + Number(x.quantity || 0), 0);

  // Chevron icon component
  const ChevronIcon = ({ isOpen }) => (
    <svg 
      className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
      fill="none" 
      stroke="currentColor" 
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );

  return (
    <header className={`w-full fixed mx-auto transition-all duration-300 xl:h-[80px] sm:h-[52px] z-20 ${getHeaderBg()}`}>
      {/* Desktop Header */}
      <div className={`hidden md:flex justify-between items-center w-full mx-auto min-h-[60px] md:min-h-[80px] px-4 md:px-6 lg:px-20 transition-all duration-300 ${!isWhite && isScrolled ? 'fixed top-0 z-50 bg-[#F7F4EF]' : ''}`}>
        {/* Left - Navigation */}
        <nav className="flex space-x-4 md:space-x-6 font-light text-[13px] md:text-[13px] lg:text-[15px]">
          <button
            className={`hover:opacity-70 transition-all px-3 md:px-4 py-1 md:py-2 rounded-md ${getTextColor()}`}
            onMouseEnter={() => handleMenuEnter('discover')}
            onMouseLeave={handleMenuLeave}
          >
            Discover
          </button>
          <button
            className={`hover:opacity-70 transition-all px-3 md:px-4 py-1 md:py-2 rounded-md ${getTextColor()}`}
            onMouseEnter={() => handleMenuEnter('boutique')}
            onMouseLeave={handleMenuLeave}
          >
            Boutique
          </button>
        </nav>

        {/* Center - Logo */}
        <div onClick={() => navigateTo('/')} className="cursor-pointer">
          {isWhite || activeMenu ? (
            <img
              src="https://cdn.builder.io/api/v1/image/assets/TEMP/b579b0acb0e565e43c6894a52d0889061ba28b8ee4f4cf227acb566a436cddbe?placeholderIfAbsent=true&apiKey=b9b4bb420cb64e36b6b71e99a06536e1"
              alt="Bodios Yak Down"
              className="h-8 md:h-10 lg:h-12 xl:h-14 w-auto"
            />
          ) : (
            <h1 className={`text-[16px] md:text-[18px] xl:text-[22px] font-semibold tracking-wide transition-colors duration-300 ${getTextColor()}`}>
              BODIOS YAK DOWN
            </h1>
          )}
        </div>

        {/* Right - Search & Cart */}
        <div className={`flex space-x-4 md:space-x-6 font-light text-[13px] md:text-[13px] lg:text-[15px] transition-colors duration-300 ${getTextColor()}`}>
          <div className="px-3 md:px-4 py-1 md:py-2">
            <SearchModal products={products} textColor={getTextColor()} />
          </div>
          <CartButtonAndPreview getTextColor={getTextColor} />
        </div>
      </div>

      {/* Desktop Dropdown Menu */}
      {activeMenu && (
        <div
          className="hidden md:flex absolute top-[80px] left-0 w-full z-20 px-12 bg-[#F7F4EF]"
          onMouseEnter={handleDropdownEnter}
          onMouseLeave={handleDropdownLeave}
        >
          {/* Left - Text Links */}
          <nav className="flex flex-col items-start space-y-4 text-black w-1/5 p-8 text-[16px] lg:text-[18px]">
            {currentMenuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigateTo(item.path)}
                className="hover:text-gray-400 transition-colors px-4 py-2 rounded-md text-left"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Right - Image Cards */}
          <div className="w-4/5 flex justify-center items-center">
            <div className="flex space-x-4 overflow-x-auto p-6">
              {currentMenuItems.map((item) => (
                <div key={item.path} className="cursor-pointer flex-shrink-0" onClick={() => navigateTo(item.path)}>
                  <div
                    className="w-[250px] h-[330px] md:w-[300px] md:h-[400px] lg:w-[375px] lg:h-[470px] bg-cover bg-center rounded-sm"
                    style={{ backgroundImage: `url(${item.image})` }}
                  />
                  <h3 className="mt-2 font-light text-black">{item.label}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <div className="md:hidden flex items-center px-4 py-2 w-full justify-between">
        {/* Burger Menu */}
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={getMobileIconColor()}>
          {mobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Logo Text */}
        <h1 
          onClick={() => navigateTo('/')}
          className={`text-center text-sm cursor-pointer ${mobileMenuOpen || isWhite ? 'text-black' : 'text-white'}`}
        >
          BODIOS YAK DOWN
        </h1>

        {/* Cart */}
        <Link to="/cart" className="relative">
          <img 
            src={Bag} 
            alt="Cart" 
            className={`w-6 h-6 transition-all ${mobileMenuOpen || isWhite ? 'brightness-0' : ''}`} 
          />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[11px] rounded-full bg-black text-white px-1">
              {cartCount}
            </span>
          )}
        </Link>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#F7F4EF] absolute top-10 left-0 w-full z-20 h-[calc(100vh-40px)] overflow-y-auto">
          <nav className="flex flex-col text-black">
            {/* Home */}
            <button
              onClick={() => navigateTo('/')}
              className="w-full text-left py-3 px-6 text-[15px] border-b border-gray-200 hover:bg-gray-100"
            >
              Home
            </button>

            {/* Discover Dropdown */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleMobileSubmenu('discover')}
                className="w-full flex items-center justify-between py-3 px-6 text-[15px]"
              >
                <span>Discover</span>
                <ChevronIcon isOpen={mobileActiveSubmenu === 'discover'} />
              </button>
              
              {mobileActiveSubmenu === 'discover' && (
                <div className="border-t border-gray-100">
                  {discoverItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigateTo(item.path)}
                      className="w-full text-left py-2.5 px-10 text-[14px] text-gray-600 hover:text-black hover:bg-gray-50 transition-colors"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Boutique Dropdown */}
            <div className="border-b border-gray-200">
              <button
                onClick={() => toggleMobileSubmenu('boutique')}
                className="w-full flex items-center justify-between py-3 px-6 text-[15px]"
              >
                <span>Boutique</span>
                <ChevronIcon isOpen={mobileActiveSubmenu === 'boutique'} />
              </button>
              
              {mobileActiveSubmenu === 'boutique' && (
                <div className="border-t border-gray-100">
                  {boutiqueItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigateTo(item.path)}
                      className="w-full text-left py-2.5 px-10 text-[14px] text-gray-600 hover:text-black hover:bg-gray-50 transition-colors"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;